import { useLocalStorage } from '@vueuse/core'

import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { PadStore, PlansRequest } from './interfaces'
import type { Pace, Pad } from '@brighterly/lib-core-types/plans/interfaces'
import type { ScopeStatus } from '@shared/lib/scope'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useDisciplineStore } from '@entities/customer'
import { parseStoredJson, safeLocalStorage } from '@shared/lib/browser'

import { usePlansApi } from '../../api/plans.hook'

const STORAGE_KEY_MATH = 'plans_math_v2'
const STORAGE_KEY_ELA = 'plans_ela_v2'

const persistMath = (state: PadStore): void => {
  const ls = useLocalStorage<Pad | null>(STORAGE_KEY_MATH, null, {
    serializer: {
      read: (v) => parseStoredJson<Pad>(v),
      write: (v) => (v ? JSON.stringify(v) : ''),
    },
  })
  const pad = toRaw(state.plansMath) as Pad | null

  ls.value = pad ? { ...pad } : null
}

const persistEla = (state: PadStore): void => {
  const ls = useLocalStorage<Pad | null>(STORAGE_KEY_ELA, null, {
    serializer: {
      read: (v) => parseStoredJson<Pad>(v),
      write: (v) => (v ? JSON.stringify(v) : ''),
    },
  })
  const pad = toRaw(state.plansEla) as Pad | null

  ls.value = pad ? { ...pad } : null
}

const reconcilePace = (paces: Pace[], currentPace: Pace['pace'] | null): Pace['pace'] | null => {
  if (currentPace !== null && paces.some(p => p.pace === currentPace)) {
    return currentPace
  }
  return paces.find(p => p.default)?.pace ?? null
}

const setPlansMath = (state: PadStore, plans: Pad | null): void => {
  state.plansMath = toRaw(plans)

  const newPaces = plans?.paces ?? []
  const reconciledPace = reconcilePace(newPaces, state.paceMath)
  if (reconciledPace !== state.paceMath) {
    state.paceMath = reconciledPace
    state.periodIndex = null
  }

  persistMath(state)
}

const setPlansEla = (state: PadStore, plans: Pad | null): void => {
  state.plansEla = toRaw(plans)

  const newPaces = plans?.paces ?? []
  const reconciledPace = reconcilePace(newPaces, state.paceEla)
  if (reconciledPace !== state.paceEla) {
    state.paceEla = reconciledPace
    state.periodIndex = null
  }

  persistEla(state)
}

const clearPlansMath = (state: PadStore): void => {
  state.plansMath = null
  state.paceMath = null
  state.periodIndex = null
  persistMath(state)
}

const clearPlansEla = (state: PadStore): void => {
  state.plansEla = null
  state.paceEla = null
  state.periodIndex = null
  persistEla(state)
}

const beginRequest = (request: PlansRequest): number => {
  request.requestId += 1
  request.status = 'loading'

  return request.requestId
}

const settleRequest = (request: PlansRequest, requestId: number, status: ScopeStatus): boolean => {
  if (request.requestId !== requestId) return false

  request.status = status
  return true
}

const dropRequest = (request: PlansRequest): void => {
  request.requestId += 1
  request.status = 'idle'
}

export const actions = {
  setLoading (this: PadStore, payload: PadStore['loading']): void {
    this.loading = payload
  },
  hydrateFromStorage (this: PadStore, discipline: Discipline): void {
    const isEla = discipline === Discipline.ELA

    if (isEla) {
      if (this._hydratedEla) return
    } else {
      if (this._hydratedMath) return
    }

    // TODO: remove after 2026-05-20 — one-time cleanup of old LS keys after bump to _v2
    safeLocalStorage.removeItem(isEla ? 'plans_ela' : 'plans_math')

    const key = isEla ? STORAGE_KEY_ELA : STORAGE_KEY_MATH

    const ls = useLocalStorage<Pad | null>(key, null, {
      serializer: {
        read: (v) => parseStoredJson<Pad>(v),
        write: (v) => (v ? JSON.stringify(v) : ''),
      },
    })

    if (isEla) {
      this.plansEla = ls.value ? { ...ls.value } : null
      this._hydratedEla = true
    } else {
      this.plansMath = ls.value ? { ...ls.value } : null
      this._hydratedMath = true
    }
  },
  clear (this: PadStore): void {
    dropRequest(this.requestMath)
    dropRequest(this.requestEla)
    clearPlansMath(this)
    clearPlansEla(this)
  },
  ensureMath (this: PadStore, to: RouteLocationNormalizedGeneric): Promise<PadStore['plansMath']> {
    const requestId = beginRequest(this.requestMath)

    return usePlansApi({ discipline: Discipline.Math, to }, {
      onSuccess: (pad: Pad) => {
        if (settleRequest(this.requestMath, requestId, 'ready')) setPlansMath(this, pad)
      },
      onError: () => {
        if (settleRequest(this.requestMath, requestId, 'error')) setPlansMath(this, null)
      },
    })
  },
  ensureEla (this: PadStore, to: RouteLocationNormalizedGeneric): Promise<PadStore['plansEla']> {
    const requestId = beginRequest(this.requestEla)

    return usePlansApi({ discipline: Discipline.ELA, to }, {
      onSuccess: (plans: Pad) => {
        if (settleRequest(this.requestEla, requestId, 'ready')) setPlansEla(this, plans)
      },
      onError: () => {
        if (settleRequest(this.requestEla, requestId, 'error')) setPlansEla(this, null)
      },
    })
  },
  setPace (this: PadStore, pace: Pace['pace'] | null): void {
    const disciplineStore = useDisciplineStore()

    if (disciplineStore.isEla) {
      this.paceEla = pace
    } else if (disciplineStore.isMath) {
      this.paceMath = pace
    }
  },
  setPeriodIndex (this: PadStore, payload: PadStore['periodIndex']): void {
    this.periodIndex = payload
  },
  setCountdownExpired (this: PadStore, payload: PadStore['countdownExpired']): void {
    this.countdownExpired = payload
  },
  setModalFaq (this: PadStore, payload: PadStore['modalFaq']): void {
    this.modalFaq = payload
  },
}
